"use client";

import { useEffect, useState } from "react";
import { BRANDS } from "@/lib/types";
import type { CategorySlug } from "@/lib/types";
import { ProductCard } from "./ProductCard";
import { LeadModal } from "./LeadModal";
import { useCatalogSelector } from "@/hooks/useCatalogSelector";
import { captureUtmParams } from "@/lib/bitrix24";
import { getBrandLogoSrc, handleBrandLogoError } from "@/lib/brandLogo";
import type { CatalogItem, ModalMode } from "@/lib/types";

interface CarvetAppProps {
  initialCatalog?: CatalogItem[];
  initialError?: string | null;
}

export function CarvetApp({
  initialCatalog = [],
  initialError = null,
}: CarvetAppProps) {
  const catalog = useCatalogSelector({ initialCatalog, initialError });
  const [menuOpen, setMenuOpen] = useState(false);
  const [navbarScrolled, setNavbarScrolled] = useState(false);
  const [searchValue, setSearchValue] = useState("");
  const [mobileSearchValue, setMobileSearchValue] = useState("");
  const [modalOpen, setModalOpen] = useState(false);
  const [modalMode, setModalMode] = useState<ModalMode>("lead");
  const [activeItem, setActiveItem] = useState<CatalogItem | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; type: string } | null>(
    null,
  );

  useEffect(() => {
    captureUtmParams();
  }, []);

  useEffect(() => {
    let isScrolled = false;
    let ticking = false;

    const onScroll = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          const scrollY = window.scrollY;
          if (scrollY > 70 && !isScrolled) {
            isScrolled = true;
            setNavbarScrolled(true);
            document.body.classList.add("navbar-scrolled-active");
          } else if (scrollY < 20 && isScrolled) {
            isScrolled = false;
            setNavbarScrolled(false);
            document.body.classList.remove("navbar-scrolled-active");
          }
          ticking = false;
        });
        ticking = true;
      }
    };

    window.addEventListener("scroll", onScroll, { passive: true });
    return () => {
      window.removeEventListener("scroll", onScroll);
      document.body.classList.remove("navbar-scrolled-active");
    };
  }, []);

  useEffect(() => {
    if (successMessage) {
      const t = setTimeout(() => setSuccessMessage(null), 7000);
      return () => clearTimeout(t);
    }
  }, [successMessage]);

  useEffect(() => {
    if (toast) {
      const t = setTimeout(() => setToast(null), 4000);
      return () => clearTimeout(t);
    }
  }, [toast]);

  const openModal = (mode: ModalMode, itemId?: string) => {
    const item = itemId
      ? catalog.db.find((i) => i.id === itemId) || null
      : null;
    setActiveItem(item);
    setModalMode(mode);
    setModalOpen(true);
    setMenuOpen(false);
  };

  const handleSearchChange = (value: string, isMobile = false) => {
    if (isMobile) {
      setMobileSearchValue(value);
    } else {
      setSearchValue(value);
    }
    catalog.handleSearchInput(value);
  };

  const handleSearchSubmit = (value: string) => {
    catalog.submitFreeTextSearch(value);
    setSearchValue(value);
    setMobileSearchValue(value);
    catalog.setShowSuggestions(false);
  };

  const renderPagination = () => {
    if (catalog.totalPages <= 1) return null;

    const pages: number[] = [];
    const startPage = Math.max(1, catalog.currentPage - 2);
    const endPage = Math.min(catalog.totalPages, startPage + 4);

    for (let p = startPage; p <= endPage; p++) {
      pages.push(p);
    }

    return (
      <div
        id="paginationContainer"
        className="pagination-wrapper"
        style={{
          display: "flex",
          justifyContent: "center",
          gap: 8,
          marginTop: 30,
          flexWrap: "wrap",
        }}
      >
        <button
          type="button"
          className="pill-btn"
          style={{ margin: "0 4px" }}
          disabled={catalog.currentPage === 1}
          onClick={() => catalog.goToPage(catalog.currentPage - 1)}
        >
          « Назад
        </button>

        {pages.map((p) => (
          <button
            key={p}
            type="button"
            className={`pill-btn ${catalog.currentPage === p ? "active" : ""}`}
            style={{ margin: "0 4px" }}
            onClick={() => catalog.goToPage(p)}
          >
            {p}
          </button>
        ))}

        <button
          type="button"
          className="pill-btn"
          style={{ margin: "0 4px" }}
          disabled={catalog.currentPage === catalog.totalPages}
          onClick={() => catalog.goToPage(catalog.currentPage + 1)}
        >
          Вперед »
        </button>
      </div>
    );
  };

  const fuelIcons: Record<string, string> = {
    Бензин: "⛽ Бензин",
    Дизель: "🛢️ Дизель",
    Гибрид: "⚡ Гибрид",
  };

  const renderCatalogContent = () => {
    if (catalog.error) {
      return (
        <div className="empty-box catalog-error-box">
          <div style={{ fontSize: 36, marginBottom: 12 }}>⚠️</div>
          <h3>Не удалось загрузить каталог</h3>
          <p
            style={{
              color: "var(--text-muted)",
              fontSize: 14,
              marginTop: 6,
            }}
          >
            {catalog.error}
          </p>
          <button
            type="button"
            className="btn btn-primary"
            style={{ marginTop: 16 }}
            onClick={() => catalog.reloadDatabase()}
          >
            <span>Повторить загрузку</span>
          </button>
        </div>
      );
    }

    if (catalog.loading) {
      return Array.from({ length: 6 }, (_, i) => (
        <div key={`skeleton-${i}`} className="product-card skeleton-card" aria-hidden="true">
          <div className="skeleton-line skeleton-line-sm" />
          <div className="skeleton-block skeleton-image" />
          <div className="skeleton-line skeleton-line-lg" />
          <div className="skeleton-line skeleton-line-md" />
          <div className="skeleton-line skeleton-line-full" />
        </div>
      ));
    }

    if (catalog.matchedItems.length === 0) {
      return (
        <div className="empty-box">
          <div style={{ fontSize: 36, marginBottom: 12 }}>🔍</div>
          <h3>
            Агрегат по выбранным параметрам пока не найден на основном
            складе
          </h3>
          <p
            style={{
              color: "var(--text-muted)",
              fontSize: 14,
              marginTop: 6,
            }}
          >
            Мы подберем и приведем нужную маркировку ДВС или КПП под заказ
            со склада партнеров за 15 минут.
          </p>
          <button
            type="button"
            className="btn btn-primary"
            style={{ marginTop: 16 }}
            onClick={() => openModal("lead")}
          >
            <span>Оформить запрос инженеру по подбору</span>
          </button>
        </div>
      );
    }

    return catalog.paginatedItems.map((item) => (
      <ProductCard
        key={item.id}
        item={item}
        onLead={(id) => openModal("lead", id)}
        onPhoto={(id) => openModal("photo", id)}
      />
    ));
  };

  return (
    <>
      <header className={`navbar ${navbarScrolled ? "navbar-scrolled" : ""}`}>
        <div className="container nav-container">
          <a href="#" className="logo">
            {/* eslint-disable-next-line @next/next/no-img-element */}
            <img src="/img/logo.png" alt="Carvet" />
            <div className="logo-text">
              <span className="brand-title">Carvet</span>
              <span className="brand-subtitle">
                контрактные Б/У ДВС, АКПП, МКПП из Кореи, ОАЭ, Японии
              </span>
            </div>
          </a>

          <div className="mobile-header-controls">
            <div className="mobile-header-search-box">
              <input
                type="text"
                id="mobileHeaderSearch"
                className="mobile-header-input"
                placeholder="Поиск по базе..."
                autoComplete="off"
                value={mobileSearchValue}
                onChange={(e) => handleSearchChange(e.target.value, true)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    handleSearchSubmit(mobileSearchValue);
                  }
                }}
              />
              <button
                type="button"
                className="mobile-header-search-btn"
                onClick={() => handleSearchSubmit(mobileSearchValue)}
              >
                🔍
              </button>
            </div>
            <button
              type="button"
              className={`burger-btn ${menuOpen ? "open" : ""}`}
              id="burgerBtn"
              aria-label="Переключить меню"
              onClick={() => setMenuOpen(!menuOpen)}
            >
              <span className="burger-bars">
                <span className="burger-bar" />
                <span className="burger-bar" />
                <span className="burger-bar" />
              </span>
              <span className="burger-label">МЕНЮ</span>
            </button>
          </div>

          <nav className={`nav-menu ${menuOpen ? "open" : ""}`} id="navMenu">
            <a
              href="#catalog"
              className="nav-menu-link"
              onClick={() => setMenuOpen(false)}
            >
              Подобрать агрегат
            </a>
            <a
              href="#resultsSection"
              className="nav-menu-link"
              onClick={() => setMenuOpen(false)}
            >
              Каталог
            </a>
            <a
              href="#toursSection"
              className="nav-menu-link"
              onClick={() => setMenuOpen(false)}
            >
              Виртуальный тур
            </a>
            <a
              href="#reviewsSection"
              className="nav-menu-link"
              onClick={() => setMenuOpen(false)}
            >
              Отзывы
            </a>
            <button
              type="button"
              className="btn-callback-nav"
              onClick={() => openModal("callback")}
            >
              <span>📞 Заказать звонок менеджера</span>
            </button>
          </nav>

          <div className="nav-actions">
            <div className="phone-block">
              <a href="tel:88001008750" className="phone-link">
                8 (800) 100-87-50
              </a>
              <span className="phone-status">● Звонок бесплатный по РФ</span>
            </div>
            <span className="or-divider">или</span>
            <button
              type="button"
              className="phone-cta-btn"
              onClick={() => openModal("callback")}
            >
              <span>📞 Заказать звонок</span>
            </button>
          </div>
        </div>
      </header>

      <section className="search-section">
        <div className="container">
          <div
            className="search-box-container"
            style={{ position: "relative", width: "100%" }}
          >
            <div className="search-box" style={{ padding: "0 0 0 16px" }}>
              <span
                style={{
                  fontSize: 18,
                  marginRight: 8,
                  color: "var(--text-muted)",
                }}
              >
                🔍
              </span>
              <input
                type="text"
                id="vinSearchInput"
                className="search-input"
                placeholder="Быстрый поиск по маркировке ДВС/КПП (G4KD, AW50), модели, объему или параметрам..."
                autoComplete="off"
                value={searchValue}
                onChange={(e) => handleSearchChange(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") handleSearchSubmit(searchValue);
                }}
              />
              <button
                type="button"
                id="btnSearchSubmit"
                className="btn btn-primary"
                style={{
                  borderRadius: "0 var(--radius-md) var(--radius-md) 0",
                  padding: "12px 24px",
                  fontSize: 14,
                  fontWeight: 700,
                }}
                onClick={() => handleSearchSubmit(searchValue)}
              >
                Найти агрегат
              </button>
            </div>
            {catalog.showSuggestions && (
              <div
                id="searchSuggestionsBox"
                className="suggestions-box"
                style={{ display: "block" }}
              >
                {catalog.suggestions.map((s, i) => (
                  <div
                    key={`${s.type}-${s.value}-${i}`}
                    className="suggestion-item"
                    role="button"
                    tabIndex={0}
                    onClick={() => {
                      if (s.type === "model") {
                        setSearchValue(s.label);
                        setMobileSearchValue(s.label);
                      } else {
                        setSearchValue(s.value);
                        setMobileSearchValue(s.value);
                      }
                      catalog.applySuggestion(s);
                    }}
                    onKeyDown={() => {}}
                  >
                    {s.label}
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="category-bar-section">
        <div className="container">
          <div className="category-tabs">
            {(
              [
                ["all", "Все агрегаты"],
                ["dvs", "🚀 Двигатели в сборе (ДВС)"],
                ["kpp", "⚙️ Коробки передач (АКПП/МКПП)"],
              ] as const
            ).map(([slug, label]) => (
              <button
                key={slug}
                type="button"
                className={`category-tab-btn ${catalog.selectedCategory === slug ? "active" : ""}`}
                onClick={() => {
                  catalog.onCategoryChange(slug as CategorySlug);
                  setSearchValue("");
                  setMobileSearchValue("");
                }}
              >
                {label}
              </button>
            ))}
          </div>
        </div>
      </section>

      <section
        className="wizard-section"
        id="catalog"
        style={{ padding: "24px 0" }}
      >
        <div className="container">
          <div className="wizard-card">
            <div className="wizard-header">
              <div>
                <h2 className="wizard-title">Пошаговый подбор агрегата</h2>
                <div
                  style={{
                    fontSize: 13,
                    color: "var(--text-muted)",
                    marginTop: 2,
                  }}
                >
                  Страница автоматически прокручивается к следующему шагу при
                  выборе
                </div>
              </div>
              <button
                type="button"
                id="btnResetFilter"
                className="btn-reset-link"
                onClick={() => {
                  catalog.resetAll();
                  setSearchValue("");
                  setMobileSearchValue("");
                }}
              >
                ↺ Сбросить все фильтры
              </button>
            </div>

            <div
              className={`wizard-step-block ${catalog.activeStep === 1 ? "active pulse-anim" : ""}`}
              id="stepBlock1"
            >
              <div className="step-title-row">
                <span className="step-badge">ШАГ 1</span>
                <h3 className="step-title">Выберите марку автомобиля:</h3>
              </div>
              <div className="brand-buttons-grid">
                {BRANDS.map((brand) => (
                  <button
                    key={brand}
                    type="button"
                    className={`brand-pill-btn ${catalog.selected.brand === brand ? "active" : ""}`}
                    data-brand={brand}
                    onClick={() => {
                      catalog.clearSearch();
                      setSearchValue("");
                      setMobileSearchValue("");
                      catalog.onBrandChange(brand, true);
                    }}
                  >
                    {/* eslint-disable-next-line @next/next/no-img-element */}
                    <img
                      src={getBrandLogoSrc(brand)}
                      alt={brand}
                      className="brand-logo-icon"
                      onError={(e) => handleBrandLogoError(e, brand)}
                    />
                    <span>{brand}</span>
                  </button>
                ))}
              </div>
              <div className="select-fallback-row">
                <span>Или из списка:</span>
                <select
                  id="selectBrand"
                  className="select-control"
                  value={catalog.selected.brand}
                  onChange={(e) => {
                    catalog.clearSearch();
                    catalog.onBrandChange(e.target.value, true);
                  }}
                >
                  <option value="">-- Выберите марку --</option>
                  {catalog.brands.map((b) => (
                    <option key={b} value={b}>
                      {b}
                    </option>
                  ))}
                </select>
              </div>
            </div>

            {catalog.selected.brand && (
              <div
                className={`wizard-step-block ${catalog.activeStep === 2 ? "active pulse-anim" : ""}`}
                id="stepBlock2"
              >
                <div className="step-title-row">
                  <span className="step-badge">ШАГ 2</span>
                  <h3 className="step-title">
                    Выберите модель для {catalog.selected.brand}:
                  </h3>
                </div>
                <div className="options-buttons-grid" id="modelPillsGrid">
                  {catalog.baseModels.map((m) => (
                    <button
                      key={m}
                      type="button"
                      className={`pill-btn ${catalog.selected.model === m ? "active" : ""}`}
                      onClick={() => {
                        catalog.clearSearch();
                        catalog.onModelChange(m, true);
                      }}
                    >
                      {m}
                    </button>
                  ))}
                </div>
                <div className="select-fallback-row">
                  <span>Или из списка:</span>
                  <select
                    id="selectModel"
                    className="select-control"
                    value={catalog.selected.model}
                    onChange={(e) => catalog.onModelChange(e.target.value, true)}
                  >
                    <option value="">-- Выберите модель --</option>
                    {catalog.baseModels.map((m) => (
                      <option key={m} value={m}>
                        {m}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {catalog.selected.model && catalog.fuels.length > 0 && (
              <div
                className={`wizard-step-block ${catalog.activeStep === 3 ? "active pulse-anim" : ""}`}
                id="stepBlock3"
              >
                <div className="step-title-row">
                  <span className="step-badge">ШАГ 3</span>
                  <h3 className="step-title">Выберите тип топлива:</h3>
                </div>
                <div className="options-buttons-grid" id="fuelPillsGrid">
                  {catalog.fuels.map((f) => (
                    <button
                      key={f}
                      type="button"
                      className={`pill-btn pill-btn-large ${catalog.selected.fuel === f ? "active" : ""}`}
                      onClick={() => {
                        catalog.clearSearch();
                        catalog.onFuelChange(f, true);
                      }}
                    >
                      {fuelIcons[f] || f}
                    </button>
                  ))}
                </div>
                <div className="select-fallback-row">
                  <span>Или из списка:</span>
                  <select
                    id="selectFuel"
                    className="select-control"
                    value={catalog.selected.fuel}
                    onChange={(e) => catalog.onFuelChange(e.target.value, true)}
                  >
                    <option value="">-- Выберите тип топлива --</option>
                    {catalog.fuels.map((f) => (
                      <option key={f} value={f}>
                        {f}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {catalog.selected.fuel && catalog.displacements.length > 0 && (
              <div
                className={`wizard-step-block ${catalog.activeStep === 4 ? "active pulse-anim" : ""}`}
                id="stepBlock4"
              >
                <div className="step-title-row">
                  <span className="step-badge">ШАГ 4</span>
                  <h3 className="step-title">Выберите объем двигателя:</h3>
                </div>
                <div className="options-buttons-grid" id="dispPillsGrid">
                  {catalog.displacements.map((d) => (
                    <button
                      key={d}
                      type="button"
                      className={`pill-btn ${catalog.selected.displacement === d ? "active" : ""}`}
                      onClick={() => {
                        catalog.clearSearch();
                        catalog.onDisplacementChange(d, true);
                      }}
                    >
                      {d}
                    </button>
                  ))}
                </div>
                <div className="select-fallback-row">
                  <span>Или из списка:</span>
                  <select
                    id="selectDisplacement"
                    className="select-control"
                    value={catalog.selected.displacement}
                    onChange={(e) =>
                      catalog.onDisplacementChange(e.target.value, true)
                    }
                  >
                    <option value="">-- Выберите объем --</option>
                    {catalog.displacements.map((d) => (
                      <option key={d} value={d}>
                        {d}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}

            {catalog.selected.displacement && catalog.powers.length > 0 && (
              <div
                className={`wizard-step-block ${catalog.activeStep === 5 ? "active pulse-anim" : ""}`}
                id="stepBlock5"
              >
                <div className="step-title-row">
                  <span className="step-badge">ШАГ 5</span>
                  <h3 className="step-title">Выберите мощность (л.с.):</h3>
                </div>
                <div className="options-buttons-grid" id="powerPillsGrid">
                  {catalog.powers.map((p) => (
                    <button
                      key={p}
                      type="button"
                      className={`pill-btn ${catalog.selected.power === p ? "active" : ""}`}
                      onClick={() => {
                        catalog.clearSearch();
                        catalog.onPowerChange(p, true);
                      }}
                    >
                      {p}
                    </button>
                  ))}
                </div>
                <div className="select-fallback-row">
                  <span>Или из списка:</span>
                  <select
                    id="selectPower"
                    className="select-control"
                    value={catalog.selected.power}
                    onChange={(e) => catalog.onPowerChange(e.target.value, true)}
                  >
                    <option value="">-- Выберите мощность --</option>
                    {catalog.powers.map((p) => (
                      <option key={p} value={p}>
                        {p}
                      </option>
                    ))}
                  </select>
                </div>
              </div>
            )}
          </div>
        </div>
      </section>

      <section className="results-section" id="resultsSection">
        <div className="container">
          <div className="results-header">
            <div>
              <h3 className="results-title">Каталог контрактных агрегатов</h3>
              <div
                style={{ fontSize: 13, color: "var(--text-muted)" }}
                id="currentFilterSummary"
              >
                {catalog.filterSummary}
              </div>
            </div>
            <span className="results-count" id="resultsCount">
              {catalog.loading
                ? "Загрузка..."
                : `${catalog.matchedItems.length} позиций`}
            </span>
          </div>

          <div className="results-grid" id="resultsGrid">
            {renderCatalogContent()}
          </div>

          {!catalog.loading && !catalog.error && renderPagination()}
        </div>
      </section>

      <section
        className="tours-section"
        id="toursSection"
        style={{
          background: "#ffffff",
          borderTop: "1px solid var(--border-color)",
          padding: "50px 0",
        }}
      >
        <div className="container">
          <h2
            style={{
              fontSize: 24,
              fontWeight: 800,
              textAlign: "center",
              marginBottom: 12,
              color: "var(--text-main)",
            }}
          >
            Виртуальный тур по филиалам CARVET
          </h2>
          <p
            style={{
              textAlign: "center",
              color: "var(--text-muted)",
              fontSize: 14,
              marginBottom: 30,
              maxWidth: 600,
              marginLeft: "auto",
              marginRight: "auto",
            }}
          >
            Посмотрите вживую на наши офисы и чистые склады контрактных ДВС и
            трансмиссий в Челябинске и Москве.
          </p>

          <div className="tours-grid">
            {[
              {
                title: "📍 Офис Челябинск",
                src: "https://www.google.com/maps/embed?pb=!4v1755255504501!6m8!1m7!1sCAoSF0NJSE0wb2dLRUlDQWdJRHEzTUhtMkFF!2m2!1d55.12101905429805!2d61.38703910979986!3f1.329173772708316!4f-1.7648401657767323!5f0.7820865974627469",
              },
              {
                title: "📦 Склад Челябинск",
                src: "https://www.google.com/maps/embed?pb=!4v1755256223176!6m8!1m7!1sCAoSFkNJSE0wb2dLRUlDQWdJQ0pnTk9GQWc.!2m2!1d55.12192330419439!2d61.38922784017993!3f174.90647314136248!4f2.3853053966523134!5f0.7820865974627469",
              },
              {
                title: "📍 Офис Москва",
                src: "https://www.google.com/maps/embed?pb=!4v1752046170367!6m8!1m7!1sCAoSFkNJSE0wb2dLRUlDQWdJQ0pqc21MY3c.!2m2!1d55.60999238145281!2d37.65241301829153!3f148.1773155025537!4f-3.728879530280466!5f0.7820865974627469",
              },
              {
                title: "📦 Склад Москва",
                src: "https://www.google.com/maps/embed?pb=!4v1752046644859!6m8!1m7!1sCAoSFkNJSE0wb2dLRUlDQWdJQ0pqb21xQ0E.!2m2!1d55.60978099265746!2d37.65192215202992!3f142.6875955136292!4f5.781210557192992!5f0.7820865974627469",
              },
            ].map((tour) => (
              <div key={tour.title} className="tour-card">
                <div className="tour-header">{tour.title}</div>
                <div className="tour-embed">
                  <iframe
                    src={tour.src}
                    style={{ border: 0, width: "100%", height: "100%" }}
                    allowFullScreen
                    loading="lazy"
                    referrerPolicy="no-referrer-when-downgrade"
                    title={tour.title}
                  />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section
        className="reviews-section"
        id="reviewsSection"
        style={{
          background: "#f8fafc",
          borderTop: "1px solid var(--border-color)",
          padding: "50px 0",
        }}
      >
        <div className="container">
          <h2
            style={{
              fontSize: 24,
              fontWeight: 800,
              textAlign: "center",
              marginBottom: 30,
              color: "var(--text-main)",
            }}
          >
            Отзывы о компании CARVET
          </h2>
          <div
            style={{
              borderRadius: "var(--radius-lg)",
              overflow: "hidden",
              border: "1px solid var(--border-color)",
              background: "#fff",
              boxShadow: "var(--shadow-md)",
            }}
          >
            <iframe
              src="https://swdgts.ru/bacb2a22934dca5610240caf8815c3c3"
              width="100%"
              height={850}
              style={{ border: 0 }}
              title="Отзывы CARVET"
            />
          </div>
        </div>
      </section>

      <LeadModal
        isOpen={modalOpen}
        mode={modalMode}
        item={activeItem}
        selected={catalog.selected}
        onClose={() => setModalOpen(false)}
        onSuccess={setSuccessMessage}
        onError={(msg) => setToast({ msg, type: "error" })}
      />

      <div
        className={`global-alert ${successMessage ? "show" : ""}`}
        id="globalSuccessAlert"
      >
        <span style={{ fontSize: 18 }}>✅</span>
        <span className="alert-msg">{successMessage}</span>
      </div>

      {toast && (
        <div className={`toast toast-${toast.type} show`}>{toast.msg}</div>
      )}

      <div className="mobile-sticky-bar">
        <a href="tel:88001008750" className="mobile-sticky-btn btn-call">
          <span>📞 Звонок 8(800)</span>
        </a>
        <button
          type="button"
          className="mobile-sticky-btn btn-chat"
          onClick={() => openModal("callback")}
        >
          <span>💬 Заказать звонок</span>
        </button>
      </div>
    </>
  );
}
